import { get, post, put, patch, json, apiCache } from './apiClient';

export interface TemplateMeta { id: string; renderer?: string; spec?: any }

export async function getTemplatesMeta(force?: boolean) {
  const cacheKey = 'templates_meta_v1';
  if (!force) {
    const cached = apiCache.get(cacheKey);
    if (cached) return cached as TemplateMeta[];
  }
  const res = await get<TemplateMeta[]>('/api/templates?format=meta', { soft: true });
  const list = Array.isArray(res.data) ? res.data : [];
  apiCache.set(cacheKey, list);
  return list;
}

export async function getSiteSettings() {
  const backoffs = [300, 900]; // ms (2 retries)
  for (let attempt = 0; attempt < backoffs.length + 1; attempt++) {
    const res = await get<any>('/api/site-settings', { soft: true });
    if (res.ok && res.data) return res.data;
    if (res.status === 503 && attempt < backoffs.length) {
      await new Promise(r => setTimeout(r, backoffs[attempt]));
      continue;
    }
    return res.data || null;
  }
  return null;
}

export async function saveSiteSettings(homepageSettings: any, bearer?: string | null) {
  const body = json({ homepageSettings });
  const res = await put<any>('/api/admin/site-settings', body, { soft: true, headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined });
  return res.data || null;
}

export async function uploadBundle(file: File) {
  const form = new FormData(); form.append('bundle', file);
  const res = await fetch('/api/admin/templates/uploadBundle', { method: 'POST', body: form, credentials: 'same-origin' });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error((data as any).error || `Upload failed (${res.status})`);
  return data;
}

export async function uploadTCard(file: File, meta?: { name?: string; description?: string; category?: string; componentKey?: string }) {
  const form = new FormData(); form.append('tcard', file);
  if (meta) Object.entries(meta).forEach(([k,v]) => { if (v != null) form.append(k, String(v)); });
  const res = await fetch('/api/admin/templates/uploadTCard', { method:'POST', body: form, credentials:'same-origin' });
  const data = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error((data as any).error || `Upload failed (${res.status})`);
  return data;
}

// Fetch single template meta by id and normalize to a minimal shape
export async function getTemplateMetaById(id: string) {
  if (!id) return null;
  const res = await get<any>(`/api/templates/${encodeURIComponent(id)}`, { soft: true });
  const single = res.data || null;
  if (!single || !single.meta) return null;
  const m: any = {
    id: String(single.id || id),
    name: single.meta.name || 'Template',
    description: single.meta.description,
    category: String(single.meta.category || 'business').toLowerCase() === 'artist' ? 'Artist' : 'Business',
    renderer: (single.meta.renderer as any) || (single.spec ? 'dynamic-v1' : 'static-v1'),
    componentKey: single.meta.componentKey || '',
    spec: single.spec,
    coverUrl: single.previewUrl,
    assets: (single.meta && typeof single.meta.assets === 'object') ? single.meta.assets : undefined,
    version: String(single.meta.version || '1.0.0'),
    status: 'active',
  };
  return m;
}
